import { useCalculator } from './useCalculator'
import { useAnimatedNumber } from '@/hooks/useAnimatedNumber'
import calcContent from '@content/calculator.yaml'
import type { CalculatorContent } from '@/types/content'

const content = calcContent as unknown as CalculatorContent

export function Calculator() {
  const calc = useCalculator(content)

  const animatedTotalHrs = useAnimatedNumber(calc.result?.totalHrs ?? 0)
  const animatedReturnedHrs = useAnimatedNumber(calc.result?.returnedHrs ?? 0)
  const animatedSavedCost = useAnimatedNumber(calc.result?.savedCost ?? 0)

  return (
    <section id="calculator" className="sd">
      <div className="ey ey-lt reveal">{content.eyebrow}</div>
      <h2 className="sh2 sh2-lt reveal d2" dangerouslySetInnerHTML={{ __html: content.headline }} />
      <p className="slead slead-lt reveal d3">{content.lead}</p>

      <div className="calc-wrap">
        {/* Form */}
        <div className="calc-panel reveal">
          <p className="calc-title">{content.formTitle}</p>

          <div className="fg">
            <label className="fl">Currency</label>
            <div className="currency-toggle">
              <button className={`ctbtn${calc.currency === 'INR' ? ' active' : ''}`} onClick={() => calc.switchCurrency('INR')}>₹ INR</button>
              <button className={`ctbtn${calc.currency === 'USD' ? ' active' : ''}`} onClick={() => calc.switchCurrency('USD')}>$ USD</button>
            </div>
          </div>

          <div className="fg">
            <label className="fl">Your industry</label>
            <select className="fsel" value={calc.sector} onChange={e => calc.setSector(e.target.value)}>
              {content.sectors.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="fg">
            <div className="slider-row">
              <label className="fl" style={{ margin: 0 }}>Team members on manual tasks</label>
              <span className="sv">{calc.teamSize} people</span>
            </div>
            <input type="range" min={1} max={50} value={calc.teamSize} onChange={e => calc.setTeamSize(Number(e.target.value))} />
          </div>

          <div className="fg">
            <div className="slider-row">
              <label className="fl" style={{ margin: 0 }}>Manual hours per person / week</label>
              <span className="sv">{calc.hoursPerPerson} hrs</span>
            </div>
            <input type="range" min={1} max={40} value={calc.hoursPerPerson} onChange={e => calc.setHoursPerPerson(Number(e.target.value))} />
          </div>

          <div className="fg">
            <div className="slider-row">
              <label className="fl" style={{ margin: 0 }}>Average hourly cost</label>
              <span className="sv">{calc.config.symbol}{calc.hourlyRate.toLocaleString()} / hr</span>
            </div>
            <input type="range" min={calc.config.min} max={calc.config.max} step={calc.config.step} value={calc.hourlyRate} onChange={e => calc.setHourlyRate(Number(e.target.value))} />
          </div>

          <div className="fg">
            <label className="fl">Type of manual tasks <span style={{ color: 'var(--muted)', fontWeight: 400, textTransform: 'none' as const, letterSpacing: 0 }}>(select all that apply)</span></label>
            <div className="task-grid">
              {content.tasks.map((task, i) => (
                <button key={task.label} className={`task-chip${calc.selectedTasks.has(i) ? ' selected' : ''}`} onClick={() => calc.toggleTask(i)}>
                  {task.emoji} {task.label}
                </button>
              ))}
            </div>
          </div>

          <button className="btn-calc" onClick={calc.calculate}>{content.calculateButton}</button>
        </div>

        {/* Result */}
        <div className="calc-panel reveal d2">
          <div className="result-panel">
            {!calc.result ? (
              <div className="result-empty">
                <div className="re-icon">{content.emptyStateIcon}</div>
                <p className="re-text">{content.emptyStateText}</p>
              </div>
            ) : (
              <div className="result-populated">
                <div className="result-metrics">
                  <div className="rm">
                    <div className="rm-label">Hours lost / week</div>
                    <div className="rm-val">{animatedTotalHrs} hrs/wk</div>
                    <div className="rm-sub">across your team</div>
                  </div>
                  <div className="rm">
                    <div className="rm-label">Cost / year</div>
                    <div className="rm-val">{calc.config.symbol}{animatedSavedCost.toLocaleString()}</div>
                    <div className="rm-sub">in manual labour</div>
                  </div>
                  <div className="rm">
                    <div className="rm-label">Hours returned</div>
                    <div className="rm-val">{animatedReturnedHrs} hrs/wk</div>
                    <div className="rm-sub">per week with AI</div>
                  </div>
                </div>

                <div className="feasibility">
                  <div>
                    <div className="fs-label">Automation Feasibility</div>
                    <div className="fs-score">{calc.result.scoreLabel}</div>
                  </div>
                  <div className="fs-bar"><div className="fs-fill" style={{ width: `${calc.result.score}%` }}></div></div>
                </div>

                <div style={{ background: 'var(--dark3)', border: '1px solid var(--bd)', borderRadius: 14, padding: '1.2rem 1.4rem' }}>
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--muted)', marginBottom: '0.5rem' }}>What this means</div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--cream-dim)', lineHeight: 1.65 }}>{calc.result.insight}</p>
                </div>

                <div className="result-divider"></div>
                <a href="#book" className="btn-res">{content.bookCallButton}</a>
                <p className="res-disc">{content.disclaimer}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
